import { useState, useRef, useEffect, useCallback } from 'react';
import { allThemes } from '@/theme/themes';
import type { ThemeConfig } from '@/theme/types';

interface ThemePickerProps {
  currentThemeId: string;
  onThemeChange: (theme: ThemeConfig) => void;
}

export function ThemePicker({ currentThemeId, onThemeChange }: ThemePickerProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentTheme = allThemes.find((t) => t.id === currentThemeId) ?? allThemes[0];

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (open && containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    },
    [open],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        style={{
          background: '#334155',
          color: '#e2e8f0',
          border: '1px solid #475569',
          borderRadius: 6,
          padding: '6px 12px',
          cursor: 'pointer',
          fontSize: 13,
          fontFamily: 'system-ui, sans-serif',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: 10,
            height: 10,
            borderRadius: 3,
            background: currentTheme?.canvasBackground ?? '#1a1a2e',
            border: '1px solid #64748b',
          }}
        />
        {currentTheme?.name ?? 'Theme'}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            background: '#0f172a',
            border: '1px solid #1e293b',
            borderRadius: 8,
            padding: 4,
            minWidth: 160,
            zIndex: 30,
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          {allThemes.map((theme) => (
            <button
              key={theme.id}
              onClick={() => {
                onThemeChange(theme);
                setOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '8px 12px',
                background: theme.id === currentThemeId ? '#1e293b' : 'transparent',
                color: '#e2e8f0',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 13,
                fontFamily: 'system-ui, sans-serif',
                textAlign: 'left',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  borderRadius: 4,
                  background: theme.canvasBackground,
                  border: '1px solid #475569',
                  flexShrink: 0,
                }}
              />
              <span>{theme.name}</span>
              {theme.id === currentThemeId && (
                <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: 11 }}>
                  active
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
